import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import { AnalyticsTable } from '@/components/admin/analytics/AnalyticsTable';
import type { AnalyticsColumn } from '@/components/admin/analytics/AnalyticsTable';
import { WoWBadge } from '@/components/admin/analytics/WoWBadge';
import { TranscriptViewer } from '@/components/calls/TranscriptViewer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { prepareDateRangeForQuery } from '@/utils/date-range';
import { formatDuration, formatTimestampFull, formatTimestampShort } from '@/utils/format-call';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import type { RouterOutput } from '@trapeze/server';
import { BarChart2, Download, ExternalLink, Loader2, Phone } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { z } from 'zod';

// ─── URL search params ────────────────────────────────────────────────────────

const searchSchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  agentId: z.string().optional(),
  // Drill-down filters
  drillResolutionLabel: z.string().optional(),
  drillCallType: z.string().optional(),
  drillTransferDest: z.string().optional(),
  drillIsResolved: z.boolean().optional(),
  drillAgentId: z.string().optional(),
  drillLabel: z.string().optional(), // human-readable breadcrumb label
});

export const Route = createFileRoute('/_protected/admin/analytics')({
  component: AnalyticsPage,
  validateSearch: searchSchema,
});

// ─── Types ────────────────────────────────────────────────────────────────────

type SummaryData = RouterOutput['admin']['analytics']['getSummary'];
type LocationRow = RouterOutput['admin']['analytics']['getLocationBreakdown'][number];
type CallTypeRow = RouterOutput['admin']['analytics']['getCallTypeBreakdown'][number];
type ResolutionRow = RouterOutput['admin']['analytics']['getResolutionLabelBreakdown'][number];
type TransferRoutingData = RouterOutput['admin']['analytics']['getTransferRouting'];
type DrilldownItem = RouterOutput['admin']['analytics']['getDrilldownCalls']['items'][number];
type CallDetail = RouterOutput['calls']['getCallDetailById'];

// ─── CSV Export ───────────────────────────────────────────────────────────────

/** Escapes a cell value and wraps in quotes for CSV safety. */
function csvCell(value: string | number | null | undefined): string {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

/** Formats a WoW delta as "+1.5pp" / "-0.8pp" or blank. */
function fmtPpDelta(delta: number | null | undefined): string {
  if (delta == null) return '';
  return `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}pp`;
}

/** Formats a WoW count delta as "+15" / "-8" or blank (no unit, no decimals). */
function fmtCountDelta(delta: number | null | undefined): string {
  if (delta == null) return '';
  return `${delta >= 0 ? '+' : ''}${Math.round(delta)}`;
}

export function toAnalyticsCsv(
  summary: SummaryData | undefined,
  locationRows: LocationRow[] | undefined,
  callTypeRows: CallTypeRow[] | undefined,
  resolutionRows: ResolutionRow[] | undefined,
  transferData: TransferRoutingData | undefined,
  dateLabel: string,
  locationLabel: string
): string {
  const r = (...cells: (string | number | null | undefined)[]) => cells.map(csvCell).join(',');
  const totalLabeledFailures = (resolutionRows ?? []).reduce((sum, row) => sum + row.count, 0);

  const sections: string[][] = [];

  // Header
  sections.push([r(`Analytics Export — ${dateLabel} — ${locationLabel}`)]);

  // Summary
  sections.push([
    r('SUMMARY'),
    r('Metric', 'Value', 'WoW Delta'),
    r('Total Calls', summary?.total, fmtCountDelta(summary?.wow?.totalDelta)),
    r('Auto-Resolved', summary?.resolved, ''),
    r(
      'Auto-Resolution Rate',
      summary ? `${summary.resolutionRate.toFixed(1)}%` : '',
      fmtPpDelta(summary?.wow?.resolutionRateDelta)
    ),
    r(
      'Transfer Rate',
      summary ? `${summary.transferRate.toFixed(1)}%` : '',
      fmtPpDelta(summary?.wow?.transferRateDelta)
    ),
  ]);

  // Location breakdown
  sections.push([
    r('BY LOCATION'),
    r('Location', 'Total Calls', 'Resolved', 'Auto-Resolution Rate', 'WoW Delta (pp)'),
    ...(locationRows ?? []).map((row) =>
      r(
        row.locationName,
        row.total,
        row.resolved,
        `${row.resolutionRate.toFixed(1)}%`,
        row.wowDelta?.toFixed(1) ?? ''
      )
    ),
  ]);

  // Call type breakdown
  sections.push([
    r('BY CALL TYPE'),
    r(
      'Call Type',
      'Total Calls',
      'Unique Callers',
      'Resolved',
      'Unique Resolved',
      'Auto-Resolution Rate',
      'Share of Resolved',
      'Total Resolved',
      'WoW Delta (pp)'
    ),
    ...(callTypeRows ?? []).map((row) =>
      r(
        row.callType,
        row.total,
        row.uniqueCallers,
        row.resolved,
        row.uniqueResolved,
        `${row.resolutionRate.toFixed(1)}%`,
        `${row.shareOfResolved.toFixed(1)}%`,
        summary?.resolved ?? 0,
        row.wowDelta?.toFixed(1) ?? ''
      )
    ),
  ]);

  // Failure type breakdown
  sections.push([
    r('BY FAILURE TYPE'),
    r(
      'Failure Type',
      'Count',
      'Total Calls',
      '% of All Calls',
      'Total Failures',
      '% of Failures',
      'WoW Count Delta'
    ),
    ...(resolutionRows ?? []).map((row) =>
      r(
        row.label,
        row.count,
        summary?.total ?? 0,
        `${row.pctOfTotal.toFixed(1)}%`,
        totalLabeledFailures,
        `${row.pctOfUnresolved.toFixed(1)}%`,
        row.wowCountDelta != null ? String(row.wowCountDelta) : ''
      )
    ),
  ]);

  // Transfer routing
  sections.push([
    r('TRANSFER ROUTING'),
    r(
      'Destination',
      'Transfers',
      'Total Calls',
      '% of All Calls',
      'Total Transfers',
      '% of Transfers'
    ),
    ...(transferData?.rows ?? []).map((row) =>
      r(
        row.label,
        row.count,
        summary?.total ?? 0,
        `${row.pctOfAll.toFixed(1)}%`,
        summary?.transferred ?? 0,
        `${row.pctOfTransferred.toFixed(1)}%`
      )
    ),
  ]);

  return sections.map((s) => s.join('\n')).join('\n\n');
}

function downloadCsvFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Renders a percentage with a (numerator of denominator) subline in muted text. */
function PctWithRaw({
  pct,
  numerator,
  denominator,
  children,
}: {
  pct: number;
  numerator: number;
  denominator: number;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <div>{pct.toFixed(1)}%</div>
      <div className="text-xs text-muted-foreground">
        ({numerator.toLocaleString()} of {denominator.toLocaleString()})
      </div>
      {children}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  subValue,
  wow,
  positiveIsGood = true,
  wowUnit,
  wowDecimals,
}: {
  label: string;
  value: string | number;
  subValue?: string;
  wow: number | null | undefined;
  positiveIsGood?: boolean;
  /** WoWBadge unit (default "pp"). Use "" for count deltas. */
  wowUnit?: string;
  /** WoWBadge decimals (default 1). Use 0 for count deltas. */
  wowDecimals?: number;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
        {subValue && <div className="text-xs text-muted-foreground mt-0.5">{subValue}</div>}
        {wow !== undefined && wow !== null && (
          <WoWBadge
            delta={wow}
            positiveIsGood={positiveIsGood}
            unit={wowUnit}
            decimals={wowDecimals}
            className="mt-1"
          />
        )}
      </CardContent>
    </Card>
  );
}

function DrilldownCallList({
  items,
  total,
  isCapped,
  hasMore,
  hasPrev,
  isLoading,
  onNext,
  onPrev,
  onSelectCall,
  selectedCallId,
}: {
  items: DrilldownItem[];
  total: number;
  isCapped: boolean;
  hasMore: boolean;
  hasPrev: boolean;
  isLoading: boolean;
  onNext: () => void;
  onPrev: () => void;
  onSelectCall: (id: string) => void;
  selectedCallId: string | null;
}) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">No calls found for this filter.</div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {isCapped ? `${total.toLocaleString()}+` : total.toLocaleString()} call
          {total !== 1 ? 's' : ''}
          {isCapped && (
            <span className="ml-1 text-xs">(showing first {total.toLocaleString()})</span>
          )}
        </div>
      </div>

      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">Time</TableHead>
              <TableHead className="w-28">Patient</TableHead>
              <TableHead className="w-36">Call Type</TableHead>
              <TableHead className="w-36">Resolution</TableHead>
              <TableHead className="w-16 text-right">Duration</TableHead>
              <TableHead className="w-28">Location</TableHead>
              <TableHead>Summary</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow
                key={item.id}
                onClick={() => onSelectCall(item.id)}
                className={`cursor-pointer hover:bg-muted/50 ${selectedCallId === item.id ? 'bg-muted' : ''}`}
              >
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatTimestampShort(item.startTimestamp)}
                </TableCell>
                <TableCell className="text-xs max-w-[7rem] truncate">
                  {item.patientName ?? (
                    <span className="text-muted-foreground">{item.fromNumber ?? '—'}</span>
                  )}
                </TableCell>
                <TableCell className="text-xs">
                  {item.callType ? (
                    <Badge variant="outline" className="text-xs font-normal">
                      {item.callType}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell className="text-xs">
                  {item.resolutionLabel ? (
                    <Badge
                      variant={item.isResolved ? 'secondary' : 'destructive'}
                      className="text-xs font-normal"
                    >
                      {item.resolutionLabel}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell className="text-xs text-right text-muted-foreground whitespace-nowrap">
                  {formatDuration(item.durationMs)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[7rem] truncate">
                  {item.locationName ?? '—'}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                  {item.summary ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {(hasPrev || hasMore) && (
        <div className="flex items-center justify-between pt-1">
          <Button size="sm" variant="outline" onClick={onPrev} disabled={!hasPrev}>
            Previous
          </Button>
          <Button size="sm" variant="outline" onClick={onNext} disabled={!hasMore}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

function CallDetailSheet({
  callId,
  open,
  onClose,
  isSuperAdmin,
}: {
  callId: string | null;
  open: boolean;
  onClose: () => void;
  isSuperAdmin: boolean;
}) {
  const { data, isLoading } = trpc.calls.getCallDetailById.useQuery(
    { callId: callId ?? '' },
    { enabled: !!callId && open }
  );

  const detail = data as (CallDetail & { structuredTranscript: unknown }) | null | undefined;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[680px] sm:max-w-[680px] p-0 flex flex-col overflow-hidden">
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <SheetTitle className="text-base">Call Detail</SheetTitle>
          {callId && (
            <a
              href={`${window.location.origin}/calls/${callId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              Open in dashboard
            </a>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !detail ? (
            <div className="text-center py-12 text-muted-foreground">Call not found.</div>
          ) : (
            <div className="space-y-4">
              {/* Meta */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {detail.startTimestamp && (
                  <div>
                    <div className="text-xs text-muted-foreground">Date</div>
                    <div>{formatTimestampFull(detail.startTimestamp)}</div>
                  </div>
                )}
                {detail.durationMs != null && (
                  <div>
                    <div className="text-xs text-muted-foreground">Duration</div>
                    <div>
                      {Math.round(detail.durationMs / 1000 / 60)}m{' '}
                      {Math.round((detail.durationMs / 1000) % 60)}s
                    </div>
                  </div>
                )}
                {detail.resolutionLabel && (
                  <div>
                    <div className="text-xs text-muted-foreground">Resolution</div>
                    <Badge
                      variant={detail.isResolved ? 'secondary' : 'destructive'}
                      className="text-xs mt-0.5"
                    >
                      {detail.resolutionLabel}
                    </Badge>
                  </div>
                )}
                {detail.fromNumber && (
                  <div>
                    <div className="text-xs text-muted-foreground">From</div>
                    <div className="font-mono text-xs">{detail.fromNumber}</div>
                  </div>
                )}
              </div>

              {/* Summary */}
              {detail.summary && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Summary</div>
                  <p className="text-sm">{detail.summary}</p>
                </div>
              )}

              {/* Resolution Reasoning */}
              {detail.resolutionLabelReasoning && (
                <div className="bg-muted/50 rounded-md px-3 py-2">
                  <div className="text-xs text-muted-foreground mb-1">Resolution Reasoning</div>
                  <p className="text-sm">{detail.resolutionLabelReasoning}</p>
                </div>
              )}

              {/* Transcript */}
              <div>
                <div className="text-xs text-muted-foreground mb-2">Transcript</div>
                <TranscriptViewer
                  transcript={
                    Array.isArray(detail.structuredTranscript)
                      ? (detail.structuredTranscript as Parameters<
                          typeof TranscriptViewer
                        >[0]['transcript'])
                      : null
                  }
                  isSuperAdmin={isSuperAdmin}
                />
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function AnalyticsPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const router = useRouter();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  // Redirect non-super admins
  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // ── Date range state (synced with URL) ────────────────────────────────────
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    if (search.startDate || search.endDate) {
      return {
        from: search.startDate ? new Date(search.startDate) : undefined,
        to: search.endDate ? new Date(search.endDate) : undefined,
      };
    }
    // Default: last 7 days
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 6);
    return { from, to };
  });

  const [agentId, setAgentId] = useState<string>(search.agentId ?? 'all');
  // Keyset cursor stack for drilldown pagination
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);

  const resetDrillCursor = () => {
    setCursorStack([]);
    setCurrentCursor(undefined);
  };

  // Drill-down context from URL
  const drill = {
    resolutionLabel: search.drillResolutionLabel,
    callType: search.drillCallType,
    transferDest: search.drillTransferDest,
    isResolved: search.drillIsResolved,
    agentId: search.drillAgentId,
    label: search.drillLabel,
  };
  const hasDrill = !!(
    drill.resolutionLabel ||
    drill.callType ||
    drill.transferDest ||
    drill.isResolved !== undefined ||
    drill.agentId
  );

  const { startDate, endDate } = prepareDateRangeForQuery(dateRange?.from, dateRange?.to);
  const apiAgentId = agentId === 'all' ? undefined : agentId;

  // Compute the prior period label for WoW transparency
  const wowComparisonLabel = (() => {
    if (!dateRange?.from || !dateRange?.to) return null;
    const durationMs = dateRange.to.getTime() - dateRange.from.getTime() + 86_400_000;
    const priorFrom = new Date(dateRange.from.getTime() - durationMs);
    const priorTo = new Date(dateRange.from.getTime() - 86_400_000);
    const fmt = (d: Date) =>
      d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fmt(priorFrom)} – ${fmt(priorTo)}`;
  })();

  // ── Sync filters to URL ───────────────────────────────────────────────────
  const syncFiltersToUrl = (newRange: DateRange | undefined, newAgentId: string) => {
    const { startDate: sd, endDate: ed } = prepareDateRangeForQuery(newRange?.from, newRange?.to);
    navigate({
      search: (prev) => ({
        ...prev,
        startDate: sd,
        endDate: ed,
        agentId: newAgentId === 'all' ? undefined : newAgentId,
        // Reset drill-down when filters change
        drillResolutionLabel: undefined,
        drillCallType: undefined,
        drillTransferDest: undefined,
        drillIsResolved: undefined,
        drillAgentId: undefined,
        drillLabel: undefined,
      }),
    });
    resetDrillCursor();
    setSelectedCallId(null);
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
    syncFiltersToUrl(range, agentId);
  };

  const handleAgentChange = (value: string) => {
    setAgentId(value);
    syncFiltersToUrl(dateRange, value);
  };

  const clearDrill = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        drillResolutionLabel: undefined,
        drillCallType: undefined,
        drillTransferDest: undefined,
        drillIsResolved: undefined,
        drillAgentId: undefined,
        drillLabel: undefined,
      }),
    });
    resetDrillCursor();
    setSelectedCallId(null);
  };

  const drillInto = (params: {
    resolutionLabel?: string;
    callType?: string;
    transferDest?: string;
    isResolved?: boolean;
    agentId?: string;
    label: string;
  }) => {
    navigate({
      search: (prev) => ({
        ...prev,
        drillResolutionLabel: params.resolutionLabel,
        drillCallType: params.callType,
        drillTransferDest: params.transferDest,
        drillIsResolved: params.isResolved,
        drillAgentId: params.agentId,
        drillLabel: params.label,
      }),
    });
    resetDrillCursor();
    setSelectedCallId(null);
  };

  // ── tRPC queries ──────────────────────────────────────────────────────────
  const analyticsInput = { startDate, endDate, agentId: apiAgentId };

  const { data: agents, isLoading: agentsLoading } =
    trpc.admin.analytics.getAvailableAgents.useQuery();

  const { data: summary, isLoading: summaryLoading } =
    trpc.admin.analytics.getSummary.useQuery(analyticsInput);

  const { data: locationRows, isLoading: locationLoading } =
    trpc.admin.analytics.getLocationBreakdown.useQuery(analyticsInput);

  const { data: callTypeRows, isLoading: callTypeLoading } =
    trpc.admin.analytics.getCallTypeBreakdown.useQuery(analyticsInput);

  const { data: resolutionRows, isLoading: resolutionLoading } =
    trpc.admin.analytics.getResolutionLabelBreakdown.useQuery(analyticsInput);

  const { data: transferData, isLoading: transferLoading } =
    trpc.admin.analytics.getTransferRouting.useQuery(analyticsInput);

  const { data: drilldownData, isLoading: drillLoading } =
    trpc.admin.analytics.getDrilldownCalls.useQuery(
      {
        startDate,
        endDate,
        agentId: drill.agentId ?? apiAgentId,
        resolutionLabel: drill.resolutionLabel,
        callType: drill.callType,
        transferDestination: drill.transferDest,
        isResolved: drill.isResolved,
        cursor: currentCursor,
        pageSize: 25,
      },
      { enabled: hasDrill }
    );

  const handleDrillNext = () => {
    if (drilldownData?.nextCursor) {
      setCursorStack((prev) => [...prev, currentCursor ?? '']);
      setCurrentCursor(drilldownData.nextCursor);
      setSelectedCallId(null);
    }
  };

  const handleDrillPrev = () => {
    const newStack = [...cursorStack];
    const prevCursor = newStack.pop();
    setCursorStack(newStack);
    setCurrentCursor(prevCursor || undefined);
    setSelectedCallId(null);
  };

  // ── CSV Export ────────────────────────────────────────────────────────────
  const isAnyDataLoading =
    summaryLoading || locationLoading || callTypeLoading || resolutionLoading || transferLoading;

  const handleExportCsv = () => {
    const fmt = (d: Date) =>
      d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const dateLabel =
      dateRange?.from && dateRange?.to
        ? `${fmt(dateRange.from)} – ${fmt(dateRange.to)}`
        : 'All dates';
    const locationLabel =
      agentId === 'all'
        ? 'All locations'
        : (agents?.find((a) => a.retellAgentId === agentId)?.locationName ?? agentId);

    const content = toAnalyticsCsv(
      summary,
      locationRows,
      callTypeRows,
      resolutionRows,
      transferData,
      dateLabel,
      locationLabel
    );

    const fileDate =
      dateRange?.from && dateRange?.to
        ? `${dateRange.from.toISOString().slice(0, 10)}_to_${dateRange.to.toISOString().slice(0, 10)}`
        : 'all-time';
    downloadCsvFile(content, `analytics-${fileDate}.csv`);
  };

  // ── Column definitions ────────────────────────────────────────────────────

  const locationColumns: AnalyticsColumn<LocationRow>[] = [
    { key: 'locationName', label: 'Location', sortable: false },
    { key: 'total', label: 'Calls', align: 'right', sortable: true },
    { key: 'resolved', label: 'Resolved', align: 'right', sortable: true },
    {
      key: 'resolutionRate',
      label: 'Auto-Res %',
      align: 'right',
      sortable: true,
      render: (row) => (
        <div>
          <div>{row.resolutionRate.toFixed(1)}%</div>
          <WoWBadge delta={row.wowDelta} />
        </div>
      ),
    },
  ];

  const callTypeColumns: AnalyticsColumn<CallTypeRow>[] = [
    { key: 'callType', label: 'Call Type', sortable: false },
    { key: 'total', label: 'Calls', align: 'right', sortable: true },
    { key: 'uniqueCallers', label: 'Unique Callers', align: 'right', sortable: true },
    { key: 'resolved', label: 'Resolved', align: 'right', sortable: true },
    { key: 'uniqueResolved', label: 'Unique Resolved', align: 'right', sortable: true },
    {
      key: 'resolutionRate',
      label: 'Auto-Res %',
      align: 'right',
      sortable: true,
      render: (row) => (
        <PctWithRaw pct={row.resolutionRate} numerator={row.resolved} denominator={row.total}>
          <WoWBadge delta={row.wowDelta} />
        </PctWithRaw>
      ),
    },
    {
      key: 'shareOfResolved',
      label: 'Share of Resolved',
      align: 'right',
      sortable: true,
      render: (row) => (
        <PctWithRaw
          pct={row.shareOfResolved}
          numerator={row.resolved}
          denominator={summary?.resolved ?? 0}
        />
      ),
    },
  ];

  const totalLabeledFailures = (resolutionRows ?? []).reduce((sum, row) => sum + row.count, 0);

  const resolutionColumns: AnalyticsColumn<ResolutionRow>[] = [
    { key: 'label', label: 'Failure Type', sortable: false },
    {
      key: 'count',
      label: 'Count',
      align: 'right',
      sortable: true,
      render: (row) => (
        <div>
          <div>{row.count}</div>
          <WoWBadge delta={row.wowCountDelta} unit="" decimals={0} positiveIsGood={false} />
        </div>
      ),
    },
    {
      key: 'pctOfTotal',
      label: '% of All Calls',
      align: 'right',
      sortable: true,
      render: (row) => (
        <PctWithRaw pct={row.pctOfTotal} numerator={row.count} denominator={summary?.total ?? 0} />
      ),
    },
    {
      key: 'pctOfUnresolved',
      label: '% of Failures',
      align: 'right',
      sortable: true,
      render: (row) => (
        <PctWithRaw
          pct={row.pctOfUnresolved}
          numerator={row.count}
          denominator={totalLabeledFailures}
        />
      ),
    },
  ];

  type TransferRow = NonNullable<TransferRoutingData>['rows'][number];
  const transferColumns: AnalyticsColumn<TransferRow>[] = [
    { key: 'label', label: 'Destination', sortable: false },
    { key: 'count', label: 'Transfers', align: 'right', sortable: true },
    {
      key: 'pctOfAll',
      label: '% of All Calls',
      align: 'right',
      sortable: true,
      render: (row) => (
        <PctWithRaw pct={row.pctOfAll} numerator={row.count} denominator={summary?.total ?? 0} />
      ),
    },
    {
      key: 'pctOfTransferred',
      label: '% of Transfers',
      align: 'right',
      sortable: true,
      render: (row) => (
        <PctWithRaw
          pct={row.pctOfTransferred}
          numerator={row.count}
          denominator={summary?.transferred ?? 0}
        />
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  if (isAdminLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="Analytics"
      description="Auto-resolution rates, failure distribution, and call volume for the AI receptionist"
      icon={BarChart2}
      isSuperAdminPage
    >
      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <DateRangePicker dateRange={dateRange} setDateRange={handleDateRangeChange} />
        <Select value={agentId} onValueChange={handleAgentChange} disabled={agentsLoading}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All locations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All locations</SelectItem>
            {agents?.map((a) => (
              <SelectItem key={a.retellAgentId} value={a.retellAgentId}>
                {a.locationName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCsv}
          disabled={isAnyDataLoading}
          className="ml-auto"
        >
          <Download className="h-4 w-4 mr-1.5" />
          Export CSV
        </Button>
      </div>

      {/* ── Summary Cards ───────────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            label="Total Calls"
            value={(summary?.total ?? 0).toLocaleString()}
            wow={summary?.wow?.totalDelta ?? null}
            positiveIsGood
            wowUnit=""
            wowDecimals={0}
          />
          <SummaryCard
            label="Auto-Resolved"
            value={(summary?.resolved ?? 0).toLocaleString()}
            subValue={`of ${(summary?.total ?? 0).toLocaleString()} calls`}
            wow={null}
            positiveIsGood
          />
          <SummaryCard
            label="Auto-Resolution Rate"
            value={summaryLoading ? '–' : `${(summary?.resolutionRate ?? 0).toFixed(1)}%`}
            wow={summary?.wow?.resolutionRateDelta ?? null}
            positiveIsGood
          />
          <SummaryCard
            label="Transfer Rate"
            value={summaryLoading ? '–' : `${(summary?.transferRate ?? 0).toFixed(1)}%`}
            wow={summary?.wow?.transferRateDelta ?? null}
            positiveIsGood={false}
          />
        </div>
        {wowComparisonLabel && (
          <p className="text-xs text-muted-foreground">
            Arrows compare to prior period: {wowComparisonLabel}
          </p>
        )}
      </div>

      {/* ── Analytics Tables ─────────────────────────────────────────────── */}
      <div className="space-y-8">
        {/* Location Breakdown */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b">
            <h3 className="text-base font-semibold">By Location</h3>
            <span className="text-xs text-muted-foreground">Click a row to filter by location</span>
          </div>
          <AnalyticsTable
            columns={locationColumns}
            rows={locationRows ?? []}
            isLoading={locationLoading}
            getRowKey={(r) => r.retellAgentId}
            onRowClick={(r) => handleAgentChange(r.retellAgentId)}
            activeRowKey={apiAgentId}
          />
        </div>

        {/* Call Type Breakdown */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b">
            <h3 className="text-base font-semibold">By Call Type</h3>
            <span className="text-xs text-muted-foreground">Click a row to drill down</span>
          </div>
          <AnalyticsTable
            columns={callTypeColumns}
            rows={callTypeRows ?? []}
            isLoading={callTypeLoading}
            getRowKey={(r) => r.callType}
            onRowClick={(r) => drillInto({ callType: r.callType, label: r.callType })}
            activeRowKey={drill.callType}
          />
        </div>

        {/* Failure Type Breakdown */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b">
            <h3 className="text-base font-semibold">By Failure Type</h3>
            <span className="text-xs text-muted-foreground">Click a row to drill down</span>
          </div>
          <AnalyticsTable
            columns={resolutionColumns}
            rows={resolutionRows ?? []}
            isLoading={resolutionLoading}
            getRowKey={(r) => r.label}
            onRowClick={(r) => drillInto({ resolutionLabel: r.label, label: r.label })}
            activeRowKey={drill.resolutionLabel}
          />
        </div>

        {/* Transfer Routing */}
        <div className="space-y-3">
          <div className="flex items-center justify-between pb-2 border-b">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold">Transfer Routing</h3>
              <span className="text-xs text-muted-foreground">Click a row to drill down</span>
            </div>
            {transferData && (
              <div className="text-sm text-muted-foreground flex items-center gap-1">
                <Phone className="h-3.5 w-3.5" />
                <span>{transferData.transferRate.toFixed(1)}% transfer rate</span>
                <WoWBadge
                  delta={transferData.wowTransferRateDelta ?? null}
                  positiveIsGood={false}
                />
              </div>
            )}
          </div>
          <AnalyticsTable
            columns={transferColumns}
            rows={transferData?.rows ?? []}
            isLoading={transferLoading}
            getRowKey={(r) => r.destination}
            onRowClick={(r) => drillInto({ transferDest: r.destination, label: r.label })}
            activeRowKey={drill.transferDest}
          />
        </div>
      </div>

      {/* ── Drill-down Call List ─────────────────────────────────────────── */}
      {hasDrill && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Calls — {drill.label ?? 'Filtered'}</CardTitle>
              <Button size="sm" variant="ghost" onClick={clearDrill}>
                Clear filter
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <DrilldownCallList
              items={drilldownData?.items ?? []}
              total={drilldownData?.total ?? 0}
              isCapped={drilldownData?.isCapped ?? false}
              hasMore={drilldownData?.hasMore ?? false}
              hasPrev={cursorStack.length > 0}
              isLoading={drillLoading}
              onNext={handleDrillNext}
              onPrev={handleDrillPrev}
              onSelectCall={setSelectedCallId}
              selectedCallId={selectedCallId}
            />
          </CardContent>
        </Card>
      )}

      {/* ── Call Detail Sheet ────────────────────────────────────────────── */}
      <CallDetailSheet
        callId={selectedCallId}
        open={!!selectedCallId}
        onClose={() => setSelectedCallId(null)}
        isSuperAdmin={isSuperAdmin}
      />
    </AdminPageLayout>
  );
}
